/**
 * The shell's real keyboard shortcuts in one place: CommandPaletteProvider
 * binds them, Help → Shortcuts lists them, and the Quick actions previews name
 * them (design system Shell · 05: ⌘K for actions, / for records, ? for help).
 * The provider accepts ⌘ or Ctrl, so labels follow the platform: ⌘ on Apple
 * devices, Ctrl everywhere else.
 */
const platform = typeof navigator === 'undefined'
  ? ''
  : (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || navigator.userAgent
const APPLE = /mac|iphone|ipad|ipod/i.test(platform)

/** The command modifier as this platform labels it. */
export const MOD_KEY = APPLE ? '⌘' : 'Ctrl'

/** One chip's worth of the Quick actions shortcut: "⌘K" or "Ctrl K". */
export const QUICK_ACTIONS_SHORTCUT = APPLE ? '⌘K' : 'Ctrl K'

export interface Shortcut {
  /** Each key renders as its own chip, pressed together. */
  keys: string[]
  label: string
  /** What a screen reader says when the chips are not one chord. */
  spoken?: string
}

const SPOKEN: Record<string, string> = { '⌘': 'Command', '↵': 'Enter', esc: 'Escape', '/': 'Slash', '?': 'Question mark' }

/** A shortcut as a screen reader should say it: "Ctrl K", "Command Enter". */
export function spokenKeys(shortcut: Shortcut): string {
  return shortcut.spoken ?? shortcut.keys.map((key) => SPOKEN[key] ?? key).join(' ')
}

export const SHORTCUT_GROUPS: ReadonlyArray<{ label: string; note?: string; shortcuts: Shortcut[] }> = [
  {
    label: 'Anywhere',
    note: '/ and ? type normally inside a text field.',
    shortcuts: [
      { keys: ['/'], label: 'Search records and pages' },
      { keys: [MOD_KEY, 'K'], label: 'Open or close quick actions' },
      { keys: ['?'], label: 'Open Help' },
    ],
  },
  {
    label: 'In search and quick actions',
    shortcuts: [
      { keys: ['↑', '↓'], label: 'Move the highlight', spoken: 'Up or Down arrow' },
      { keys: ['↵'], label: 'Open details or run the action' },
      { keys: ['esc'], label: 'Back to results, then close' },
    ],
  },
  {
    label: 'Needs your attention',
    shortcuts: [{ keys: [MOD_KEY, '↵'], label: 'Send your reply or answer' }],
  },
]
